import { useId, useMemo, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { FieldList, FieldRow, useActorNames, type Actor } from '@/shared/details'

import type { CustomFieldDefinition, CustomFieldValue } from './types'
import { groupCustomFields } from './values'

/**
 * A record's custom fields on its details page, by display group: the record's own fields
 * first, then each group under its heading, each field a `FieldRow` from the mockups'
 * `details/UserDetails.jsx`. A person shows by name, through Site Management's name lookup.
 * `group` draws one group only, as on the form.
 */
export function CustomFieldDetails(props: {
  readonly definitions: readonly CustomFieldDefinition[]
  readonly values: readonly CustomFieldValue[]
  readonly group?: string | null
}) {
  const groups = groupCustomFields(props.definitions, props.group)
  // Only the people a row shows, so a disabled field's value is not looked up.
  const shown = new Set(groups.flatMap((group) => group.fields).map((field) => field.id))
  const people: Actor[] = props.values.flatMap((value) =>
    shown.has(value.fieldId) && typeof value.personId === 'string' ? [{ type: 'user' as const, id: value.personId }] : [],
  )
  const nameOf = useActorNames(people)

  const rows = (fields: readonly CustomFieldDefinition[]) => (
    <FieldList>
      {fields.map((field) => (
        <DetailsRow
          key={field.id}
          field={field}
          value={props.values.find((value) => value.fieldId === field.id)}
          nameOf={nameOf}
        />
      ))}
    </FieldList>
  )

  return (
    <>
      {groups.map((group) =>
        group.name === null ? (
          <div key="">{rows(group.fields)}</div>
        ) : (
          <GroupSection key={group.name} name={group.name}>
            {rows(group.fields)}
          </GroupSection>
        ),
      )}
    </>
  )
}

function GroupSection(props: { readonly name: string; readonly children: ReactNode }) {
  const headingId = useId()
  return (
    <section aria-labelledby={headingId} className="mt-6">
      <h3 id={headingId} className="mb-3 font-heading text-lg font-medium text-text-primary">
        {props.name}
      </h3>
      {props.children}
    </section>
  )
}

function DetailsRow(props: {
  readonly field: CustomFieldDefinition
  readonly value: CustomFieldValue | undefined
  readonly nameOf: ReturnType<typeof useActorNames>
}) {
  const intl = useIntl()
  const { field, value } = props
  const notSet = <span className="text-text-secondary">{intl.formatMessage({ id: 'customFields.notSet' })}</span>

  switch (field.type) {
    case 'text':
    case 'dropdown':
      return <FieldRow label={field.name} value={value?.text ?? notSet} />
    case 'number':
      return (
        <FieldRow
          label={field.name}
          value={value?.number === null || value?.number === undefined ? notSet : intl.formatNumber(value.number)}
        />
      )
    case 'checkbox':
      return <FieldRow label={field.name} value={intl.formatMessage({ id: value?.checked === true ? 'customFields.yes' : 'customFields.no' })} />
    case 'checkboxList':
      return (
        <FieldRow
          label={field.name}
          value={value?.choices === null || value?.choices === undefined || value.choices.length === 0 ? notSet : intl.formatList(value.choices, { type: 'conjunction' })}
        />
      )
    case 'person': {
      const id = value?.personId
      if (id === null || id === undefined) {
        return <FieldRow label={field.name} value={notSet} />
      }
      const name = props.nameOf({ type: 'user', id })
      if (name === undefined) {
        return <FieldRow label={field.name} value={<span className="text-text-secondary">{intl.formatMessage({ id: 'customFields.person.loading' })}</span>} />
      }
      const label = name?.name ?? intl.formatMessage({ id: 'customFields.person.unknown' })
      return name?.path === undefined ? (
        <FieldRow label={field.name} value={label} />
      ) : (
        <PersonLink field={field.name} label={label} path={name.path} />
      )
    }
  }
}

function PersonLink(props: { readonly field: string; readonly label: string; readonly path: string }) {
  const { label, path } = props
  const links = useMemo(() => [{ label, path }], [label, path])
  return <FieldRow label={props.field} links={links} />
}
